// components/design/multi-layer-blob.tsx
"use client"; // Komponent kliencki

import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";

// Usunięto import getBrowserName, ponieważ nie będzie już używany do warunkowania

// Typy
type Side = "left" | "right";

export interface BlobLayer {
  enabled?: boolean;
  w?: number; // bazowy rozmiar przy szerokości viewportu 1920px
  h?: number;
  to?: string;
  from?: string;
  dx?: number; // bazowe przesunięcie horyzontalne przy szerokości viewportu 1920px
  blur?: number;
  className?: string;
}

interface Props {
  side: Side;
  offset: string;
  color?: "pink" | "orange" | "blue" | undefined;
  layers: readonly BlobLayer[];
}

/* ---------------- helpers ---------------- */
const normalizeOffset = (v: string) =>
  v.startsWith("top-[") ? v : `top-[${v}%]`;

const BASE = 1920; // referencyjna szerokość viewportu
const POWER = 1.2; // >1 → wyolbrzymia skalowanie

// Funkcja pomocnicza do debouncingu
const debounce = (func: Function, delay: number) => {
  let timeout: NodeJS.Timeout;
  return function (this: any, ...args: any[]) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
};

const useViewportScale = () => {
  const [ratio, setRatio] = useState(1);

  useEffect(() => {
    const update = () => setRatio(window.innerWidth / BASE);
    const debouncedUpdate = debounce(update, 100); // Debounce, aby zmniejszyć częstotliwość aktualizacji

    update(); // Ustaw początkową wartość
    window.addEventListener("resize", debouncedUpdate);
    return () => window.removeEventListener("resize", debouncedUpdate);
  }, []);
  return Math.pow(ratio, POWER);
};

/* -------------- component --------------- */
export default function MultiLayerBlob({ side, offset, color, layers }: Props) {
  const top = normalizeOffset(offset);
  const scale = useViewportScale();

  // Usunięto stan i useEffect do wykrywania przeglądarki

  const stack = useMemo(
    () =>
      layers.map((l) => ({
        ...l,
        dx: l.dx ?? 0,
        w: l.w ?? 2430,
        h: l.h ?? 1065,
      })),
    [layers],
  );

  // Unikalne wartości blur do generowania filtrów SVG
  const uniqueBlurs = useMemo(() => {
    const blurs = new Set<number>();
    stack.forEach((l) => {
      if (l.enabled && l.blur !== undefined) {
        blurs.add(l.blur);
      }
    });
    return Array.from(blurs).sort((a, b) => a - b);
  }, [stack]);

  return (
    <>
      {/* SVG z filtrami jest renderowane ZAWSZE, ponieważ teraz jest to domyślna optymalizacja */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        className="absolute h-0 w-0" // Ukryj SVG, jest tylko do definicji filtrów
      >
        {/* Definiuj filtry SVG dla każdego potrzebnego poziomu rozmycia */}
        {uniqueBlurs.map((blurValue) => (
          <filter
            key={`safariBlur-${blurValue}`}
            id={`safariBlur-${blurValue}`}
          >
            {/* stdDeviation to w przybliżeniu połowa wartości CSS blur w pikselach */}
            <feGaussianBlur in="SourceGraphic" stdDeviation={blurValue / 2} />
          </filter>
        ))}
      </svg>
      <div
        className={clsx(
          "pointer-events-none absolute select-none",
          top,
          "left-1/2",
        )}
      >
        {stack.map(
          (
            {
              enabled = true,
              w,
              h,
              to = "#201143",
              from = "rgba(30,30,30,0)",
              dx,
              blur = 40,
              className,
            },
            i,
          ) =>
            enabled && (
              <div
                key={i}
                className={clsx("absolute", className)}
                style={{
                  width: `${w * scale}px`,
                  height: `${h * scale}px`,
                  background: `radial-gradient(closest-side, ${to} 0%, ${from} 100%)`,
                  transform: `translate(calc(-50% + ${
                    side === "left" ? "-" : ""
                  }${dx * scale}px), -50%)`,
                  // Zawsze stosuj filtr SVG
                  filter: `url(#safariBlur-${blur})`,
                }}
              />
            ),
        )}
      </div>
    </>
  );
}
